<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">

      <!-- Back Button -->
      <div style="margin-bottom: 16px;">
        <md-button class="md-default" @click="$router.back()">
          <md-icon>arrow_back</md-icon> Back to Orders
        </md-button>
      </div>

      <div v-if="!order" style="text-align:center; padding:60px 0; color:#aaa;">
        <md-icon style="font-size:3rem !important;">hourglass_empty</md-icon>
        <h4>Loading order...</h4>
      </div>

      <div v-else class="md-layout md-gutter">

        <!-- LEFT COLUMN -->
        <div class="md-layout-item md-size-70 md-small-size-100">

          <!-- Order Header -->
          <md-card style="margin-bottom:20px;">
            <md-card-header class="md-card-header-icon md-card-header-green">
              <div class="card-icon"><md-icon>receipt_long</md-icon></div>
              <h4 class="title">Order {{ order.orderNumber }}</h4>
            </md-card-header>
            <md-card-content>
              <div class="order-meta-grid">
                <div class="meta-item">
                  <span class="meta-label">Order Date</span>
                  <span class="meta-value">{{ HumanReadableDateFormat(order.created_at, true) }}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Payment Method</span>
                  <span class="meta-value">{{ capitalize(order.paymentMethod) }}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Payment Status</span>
                  <span class="status-badge" :class="'payment-' + order.paymentStatus">
                    {{ capitalize(order.paymentStatus) }}
                  </span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Order Status</span>
                  <span class="status-badge" :class="'order-' + order.orderStatus">
                    {{ capitalize(order.orderStatus) }}
                  </span>
                </div>
                <div v-if="order.razorpayOrderId" class="meta-item">
                  <span class="meta-label">Razorpay Order ID</span>
                  <span class="meta-value mono">{{ order.razorpayOrderId }}</span>
                </div>
                <div v-if="order.razorpayPaymentId" class="meta-item">
                  <span class="meta-label">Payment ID</span>
                  <span class="meta-value mono">{{ order.razorpayPaymentId }}</span>
                </div>
                <div v-if="order.paidAt" class="meta-item">
                  <span class="meta-label">Paid At</span>
                  <span class="meta-value">{{ HumanReadableDateFormat(order.paidAt, true) }}</span>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <!-- Order Items -->
          <md-card style="margin-bottom:20px;">
            <md-card-header class="md-card-header-icon md-card-header-green">
              <div class="card-icon"><md-icon>shopping_cart</md-icon></div>
              <h4 class="title">Items Ordered</h4>
            </md-card-header>
            <md-card-content>
              <div class="items-table">
                <div class="items-header">
                  <span>Product</span>
                  <span>Price</span>
                  <span>Qty</span>
                  <span>Subtotal</span>
                </div>
                <div class="item-row" v-for="item in order.items" :key="item._id">
                  <div class="item-product">
                    <img v-if="item.productImage" :src="item.productImage" class="item-thumb" />
                    <div class="item-no-img" v-else><md-icon>image_not_supported</md-icon></div>
                    <div>
                      <strong>{{ item.productName }}</strong>
                      <br>
                      <small v-if="item.offerPrice" class="price-strike">₹{{ item.price }}</small>
                    </div>
                  </div>
                  <span class="item-price">₹{{ item.finalPrice }}</span>
                  <span class="item-qty">x{{ item.quantity }}</span>
                  <span class="item-subtotal">₹{{ item.subtotal }}</span>
                </div>

                <!-- Price Summary -->
                <div class="price-summary">
                  <div class="summary-row">
                    <span>Items Total</span>
                    <span>₹{{ order.itemsTotal }}</span>
                  </div>
                  <div class="summary-row" v-if="order.shippingCharges">
                    <span>Shipping</span>
                    <span>₹{{ order.shippingCharges }}</span>
                  </div>
                  <div class="summary-row discount" v-if="order.couponDiscount">
                    <span>Coupon ({{ order.couponCode }})</span>
                    <span>-₹{{ order.couponDiscount }}</span>
                  </div>
                  <div class="summary-row discount" v-if="order.discount">
                    <span>Discount</span>
                    <span>-₹{{ order.discount }}</span>
                  </div>
                  <div class="summary-row total">
                    <span>Total</span>
                    <span>₹{{ order.totalAmount }}</span>
                  </div>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <!-- Status History -->
          <md-card>
            <md-card-header class="md-card-header-icon md-card-header-green">
              <div class="card-icon"><md-icon>timeline</md-icon></div>
              <h4 class="title">Status History</h4>
            </md-card-header>
            <md-card-content>
              <div class="timeline">
                <div
                  class="timeline-item"
                  v-for="(h, idx) in [...order.statusHistory].reverse()"
                  :key="idx"
                >
                  <div class="timeline-dot" :class="'order-' + h.status"></div>
                  <div class="timeline-content">
                    <div class="timeline-status">{{ capitalize(h.status) }}</div>
                    <div class="timeline-note" v-if="h.note">{{ h.note }}</div>
                    <div class="timeline-time">{{ HumanReadableDateFormat(h.timestamp, true) }}</div>
                  </div>
                </div>
              </div>
            </md-card-content>
          </md-card>

        </div>

        <!-- RIGHT COLUMN -->
        <div class="md-layout-item md-size-30 md-small-size-100">

          <!-- Update Status -->
          <md-card style="margin-bottom:20px;">
            <md-card-header class="md-card-header-icon md-card-header-green">
              <div class="card-icon"><md-icon>edit</md-icon></div>
              <h4 class="title">Update Status</h4>
            </md-card-header>
            <md-card-content>
              <md-field>
                <label>Order Status</label>
                <md-select v-model="statusUpdate.orderStatus">
                  <md-option value="pending">Pending</md-option>
                  <md-option value="confirmed">Confirmed</md-option>
                  <md-option value="processing">Processing</md-option>
                  <md-option value="shipped">Shipped</md-option>
                  <md-option value="delivered">Delivered</md-option>
                  <md-option value="cancelled">Cancelled</md-option>
                  <md-option value="returned">Returned</md-option>
                </md-select>
              </md-field>
              <md-field>
                <label>Note (optional)</label>
                <md-input v-model="statusUpdate.note" placeholder="e.g. Shipped via BlueDart" />
              </md-field>
              <md-button class="md-success md-block" @click="updateStatus" :disabled="isUpdating">
                <md-icon>save</md-icon>
                {{ isUpdating ? 'Updating...' : 'Update Status' }}
              </md-button>
            </md-card-content>
          </md-card>

          <!-- Customer Info -->
          <md-card style="margin-bottom:20px;">
            <md-card-header class="md-card-header-icon md-card-header-green">
              <div class="card-icon"><md-icon>person</md-icon></div>
              <h4 class="title">Customer</h4>
            </md-card-header>
            <md-card-content>
              <div class="info-list">
                <div class="info-item">
                  <md-icon>person_outline</md-icon>
                  <span>{{ order.customerName }}</span>
                </div>
                <div class="info-item">
                  <md-icon>email</md-icon>
                  <span>{{ order.customerEmail }}</span>
                </div>
                <div class="info-item">
                  <md-icon>phone</md-icon>
                  <span>{{ order.customerPhone }}</span>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <!-- Shipping Address -->
          <md-card style="margin-bottom:20px;">
            <md-card-header class="md-card-header-icon md-card-header-green">
              <div class="card-icon"><md-icon>local_shipping</md-icon></div>
              <h4 class="title">Shipping Address</h4>
            </md-card-header>
            <md-card-content>
              <div class="address-block" v-if="order.shippingAddress">
                <strong>{{ order.shippingAddress.fullName }}</strong><br>
                {{ order.shippingAddress.addressLine1 }}<br>
                <span v-if="order.shippingAddress.addressLine2">{{ order.shippingAddress.addressLine2 }},</span>
                <span v-if="order.shippingAddress.landmark">{{ order.shippingAddress.landmark }},</span><br>
                {{ order.shippingAddress.city }}, {{ order.shippingAddress.state }} - {{ order.shippingAddress.pincode }}<br>
                {{ order.shippingAddress.country }}<br>
                <br>
                <md-icon style="font-size:14px !important; vertical-align:middle;">phone</md-icon>
                {{ order.shippingAddress.phone }}
                <span v-if="order.shippingAddress.alternatePhone">
                  / {{ order.shippingAddress.alternatePhone }}
                </span>
              </div>
            </md-card-content>
          </md-card>

          <!-- Notes -->
          <md-card v-if="order.customerNotes || order.adminNotes">
            <md-card-header class="md-card-header-icon md-card-header-green">
              <div class="card-icon"><md-icon>notes</md-icon></div>
              <h4 class="title">Notes</h4>
            </md-card-header>
            <md-card-content>
              <div v-if="order.customerNotes" style="margin-bottom:10px;">
                <small style="color:#aaa; text-transform:uppercase; font-weight:600;">Customer Note</small>
                <p style="margin:4px 0 0; color:#555;">{{ order.customerNotes }}</p>
              </div>
              <div v-if="order.adminNotes">
                <small style="color:#aaa; text-transform:uppercase; font-weight:600;">Admin Note</small>
                <p style="margin:4px 0 0; color:#555;">{{ order.adminNotes }}</p>
              </div>
            </md-card-content>
          </md-card>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrderService from "../../../services/OrderService";

export default {
  name: "OrderDetail",
  data() {
    return {
      order: null,
      isUpdating: false,
      statusUpdate: { orderStatus: "", note: "" }
    };
  },
  methods: {
    getOrderDetail() {
      const orderId = this.$route.query.orderId;
      if (!orderId) { this.$toast.error('No order ID provided'); return; }

      OrderService.getOrderDetail(orderId)
        .then(res => {
          this.order = res.data.data;
          this.statusUpdate.orderStatus = this.order.orderStatus;
        })
        .catch(e => this.$toast.error(e.response?.data?.response_msg || 'Failed to load order'));
    },
    updateStatus() {
      this.isUpdating = true;
      OrderService.updateOrderStatus({
        orderId:     this.order._id,
        orderStatus: this.statusUpdate.orderStatus,
        note:        this.statusUpdate.note
      })
        .then(() => {
          this.$toast.success('Order status updated successfully');
          this.getOrderDetail();
          this.statusUpdate.note = "";
        })
        .catch(e => this.$toast.error(e.response?.data?.response_msg || 'Failed to update status'))
        .finally(() => { this.isUpdating = false; });
    },
    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  },
  beforeMount() { this.getOrderDetail(); }
};
</script>

<style scoped>
/* Meta Grid */
.order-meta-grid {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 16px; padding: 8px 0;
}
.meta-item { display: flex; flex-direction: column; gap: 4px; }
.meta-label { font-size: 0.75rem; color: #a0aec0; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.meta-value { font-size: 0.95rem; font-weight: 600; color: #2d3748; }
.meta-value.mono { font-family: monospace; font-size: 0.82rem; color: #718096; word-break: break-all; }

/* Items Table */
.items-table { width: 100%; }
.items-header {
  display: grid; grid-template-columns: 3fr 1fr 1fr 1fr;
  padding: 10px 0; border-bottom: 2px solid #e2e8f0;
  font-size: 0.78rem; font-weight: 700; color: #a0aec0; text-transform: uppercase;
}
.item-row {
  display: grid; grid-template-columns: 3fr 1fr 1fr 1fr;
  padding: 14px 0; border-bottom: 1px solid #f0f0f0; align-items: center;
}
.item-product { display: flex; align-items: center; gap: 12px; }
.item-thumb { width: 48px; height: 48px; object-fit: cover; border-radius: 8px; }
.item-no-img {
  width: 48px; height: 48px; background: #f8f8f8; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
}
.item-price, .item-qty { color: #4a5568; font-size: 0.9rem; }
.item-subtotal { font-weight: 700; color: #2d3748; }
.price-strike { text-decoration: line-through; color: #a0aec0; }

/* Price Summary */
.price-summary { margin-top: 16px; border-top: 2px solid #e2e8f0; padding-top: 12px; }
.summary-row {
  display: flex; justify-content: space-between;
  padding: 6px 0; color: #4a5568; font-size: 0.95rem;
}
.summary-row.discount { color: #38a169; }
.summary-row.total {
  font-size: 1.1rem; font-weight: 700; color: #2d3748;
  border-top: 2px solid #e2e8f0; margin-top: 8px; padding-top: 12px;
}

/* Timeline */
.timeline { padding: 8px 0; }
.timeline-item { display: flex; gap: 16px; margin-bottom: 20px; position: relative; }
.timeline-item:not(:last-child)::before {
  content: ''; position: absolute; left: 9px; top: 22px;
  width: 2px; height: calc(100% + 4px); background: #e2e8f0;
}
.timeline-dot {
  width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0;
  border: 3px solid; margin-top: 2px;
}
.timeline-content { flex: 1; }
.timeline-status { font-weight: 700; color: #2d3748; font-size: 0.95rem; }
.timeline-note { color: #718096; font-size: 0.85rem; margin-top: 2px; }
.timeline-time { color: #a0aec0; font-size: 0.78rem; margin-top: 4px; }

/* Timeline dot colors */
.order-pending    { border-color: #ed8936; background: #fef3c7; }
.order-confirmed  { border-color: #667eea; background: #ebf4ff; }
.order-processing { border-color: #4299e1; background: #ebf8ff; }
.order-shipped    { border-color: #38b2ac; background: #e6fffa; }
.order-delivered  { border-color: #48bb78; background: #f0fff4; }
.order-cancelled  { border-color: #f56565; background: #fff5f5; }
.order-returned   { border-color: #a0aec0; background: #f7fafc; }

/* Info List */
.info-list { display: flex; flex-direction: column; gap: 12px; }
.info-item { display: flex; align-items: center; gap: 10px; color: #4a5568; font-size: 0.9rem; }
.info-item .md-icon { color: #76acaf !important; font-size: 1.1rem !important; }

/* Address */
.address-block { line-height: 1.8; color: #4a5568; font-size: 0.9rem; }

/* Shared status badges */
.status-badge {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 20px;
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
}
.payment-paid     { background: rgba(72,187,120,0.15);  color: #276749; }
.payment-pending  { background: rgba(237,137,54,0.15);  color: #c05621; }
.payment-failed   { background: rgba(245,101,101,0.15); color: #c53030; }
.payment-refunded { background: rgba(160,174,192,0.2);  color: #4a5568; }
.order-pending    { background: rgba(237,137,54,0.12);  color: #c05621; }
.order-confirmed  { background: rgba(102,126,234,0.12); color: #553c9a; }
.order-processing { background: rgba(66,153,225,0.12);  color: #2b6cb0; }
.order-shipped    { background: rgba(56,178,172,0.12);  color: #234e52; }
.order-delivered  { background: rgba(72,187,120,0.15);  color: #276749; }
.order-cancelled  { background: rgba(245,101,101,0.12); color: #c53030; }
.order-returned   { background: rgba(160,174,192,0.2);  color: #4a5568; }

@media (max-width: 768px) {
  .order-meta-grid { grid-template-columns: repeat(2, 1fr); }
  .items-header, .item-row { grid-template-columns: 2fr 1fr 1fr; }
  .item-subtotal { display: none; }
}
</style>